import { Component, EventEmitter, Input, Output } from '@angular/core';
import { SelectItem } from 'primeng/api';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { Observable } from 'rxjs';
import { first, tap } from 'rxjs/operators';
import { BaseComponent } from 'src/app/core/base/base.component';
import { Utils } from 'src/app/core/utils/khuyana.utils';
import { AdminCatalogModel } from 'src/app/model/admin/catalog';
import { CommonDoctorViewModel } from 'src/app/model/common/doctor.view';
import { CommonPatientViewModel } from 'src/app/model/common/patient.view';
import { CommonTurnModel } from 'src/app/model/common/turn';
import { CommonTurnViewModel } from 'src/app/model/common/turn.view';
import { KhuyanaSelectItemModel } from 'src/app/model/core/khuyana-select-item.model';
import { SecurityUserLoginModel } from 'src/app/model/security/security-user.login';
import { PatientDialogComponent } from '../../patient/components/patient-dialog.component';
import { PatientService } from '../../patient/patient.service';
import { TURN } from '../turn.constants';
import { TurnDialogService } from './turn.dialog.service';
import { TurnPatientSearchComponent } from './turn.patient-search-dialog.component';

@Component({
  selector: 'ms-common-turn-form',
  templateUrl: './turn.form.component.html',
})
export class TurnFormComponent extends BaseComponent {
  readonly screen = TURN;
  @Input() isPatch = false;
  @Input() isPatchDoctor = false;
  @Input() isPatchStatus = false;
  @Input() isAgenda = false;
  @Input() turnId?: number;
  @Input() patientHelp?: CommonPatientViewModel;
  @Input() doctorHelp?: CommonDoctorViewModel;
  @Input() specialtyCodeAlt?: string;
  @Input() specialty?: string;
  @Input() turnStart?: Date;
  @Input() turnEnd?: Date;
  @Input() userInformation?: SecurityUserLoginModel;
  @Output() saveSubscribe = new EventEmitter<boolean>();

  turn: CommonTurnModel = {};
  patientQuery = '';
  patientsSuggestion: CommonPatientViewModel[] = [];
  specialties: AdminCatalogModel[] = [];
  specialtiesSuggestion: SelectItem<KhuyanaSelectItemModel<string>>[] = [];
  specialtyHelp?: SelectItem<KhuyanaSelectItemModel<string>>;
  doctors: CommonDoctorViewModel[] = [];
  doctorsSuggestion: CommonDoctorViewModel[] = [];
  turnStatus: AdminCatalogModel[] = [];
  turnStatusSuggestion: SelectItem<KhuyanaSelectItemModel<string>>[] = [];
  turnStatusHelp?: SelectItem<KhuyanaSelectItemModel<string>>;
  turnStatusAgenda?: AdminCatalogModel;
  turnStatusWaiting?: AdminCatalogModel;
  enableDoctorSearch = false;
  isDoctorRole = false;
  doctorOneSpecialty = false;

  constructor(
    protected ref: DynamicDialogRef,
    protected service: TurnDialogService,
    protected dialogService: DialogService,
    protected patientService: PatientService
  ) {
    super();
  }

  onInit = () => {
    this.specialtyHelp = { value: { codeAlt: this.specialtyCodeAlt } };
    const { specialties, turnStatus } = this.service.readStore();
    this.subscriptions.push(
      specialties.subscribe((result) => {
        this.specialties = result.filter((specialty) => specialty.status);
        if (this.isPatchDoctor) {
          this.specialtyHelp = this.specialties
            .map((specialty) => ({
              label: specialty.name,
              value: {
                id: specialty.id,
                codeAlt: specialty.codeAlt,
                name: specialty.name,
              },
            }))
            .find((specialty) => specialty.value.id === this.specialty);
          this.loadDoctorsBySpecialty();
        }
      }),
      turnStatus.subscribe((result) => {
        this.turnStatus = result.filter(
          (status) =>
            status.codeAlt?.toLowerCase() !== 'agenda' &&
            status.codeAlt?.toLowerCase() !== 'finish'
        );
        this.turnStatusAgenda = result.find(
          (status) => status.codeAlt?.toLowerCase() === 'vital'
        );
        this.turnStatusWaiting = result.find(
          (status) => status.codeAlt?.toLowerCase() === 'waiting'
        );
      }),
      this.service.getUserInformation().subscribe(this.subscribeUserInfo)
    );

    if (this.patientHelp) {
      this.turn.patient = this.patientHelp.id;
      this.patientsSuggestion = [this.patientHelp];
    }

    if (this.doctorHelp) {
      this.turn.doctor = this.doctorHelp.id;
      this.doctorsSuggestion = [this.doctorHelp];
    }

    if (this.isAgenda) {
      this.turn.turnStart = this.turnStart;
      this.turn.turnEnd = this.turnEnd;
    }
  };

  save = () => {
    this.loading = true;
    this.service.setLoadingState();
    let restMethod: () => Observable<CommonTurnViewModel>;

    if (!this.isPatch && !this.isPatchDoctor) {
      restMethod = this.saveNew;
    } else if (this.isPatch && this.isAgenda) {
      restMethod = this.patchAgenda;
    } else if (this.isPatch && !this.isPatchDoctor) {
      restMethod = this.patchStatus;
    } else {
      restMethod = this.patchDoctor;
    }

    this.subscriptions.push(
      restMethod().subscribe(() => this.saveSubscribe.emit(this.isDoctorRole))
    );
  };

  saveNew = (): Observable<CommonTurnViewModel> => {
    const turn: CommonTurnModel = {
      patient: this.patientHelp!.id,
      specialty: this.specialtyHelp!.value.id,
      doctor: this.doctorHelp!.id,
      turnStart: this.turn.turnStart,
      turnEnd: this.turn.turnEnd,
      isDoctorRole: this.isDoctorRole,
      balance: this.turn.balance,
    };

    if (this.isAgenda) {
      turn.turnStatus = (this.turnStatusAgenda ?? this.turnStatusWaiting)?.id;
    } else {
      turn.turnStatus = this.turnStatusHelp!.value.id;
    }

    return this.service.saveNew(turn);
  };

  patchStatus = (): Observable<CommonTurnViewModel> => {
    const turnId = this.turnId!;
    const statusId = this.turnStatusHelp!.value.id;
    const status =
      this.turnStatusHelp!.value.codeAlt?.toLowerCase() !== 'cancel';

    return this.service.patchStatus(turnId, statusId, status);
  };

  patchDoctor = (): Observable<CommonTurnViewModel> => {
    const turnId = this.turnId!;
    const doctorId = this.doctorHelp!.id;

    return this.service.patchDoctor(turnId, doctorId);
  };

  patchAgenda = (): Observable<CommonTurnViewModel> => {
    const turn: CommonTurnModel = {
      doctor: this.doctorHelp!.id,
      turnStart: this.turn.turnStart,
      turnEnd: this.turn.turnEnd,
    };
    return this.service.patchAgenda(this.turnId!, turn);
  };

  searchPatient = ($event: any) => {
    this.patientQuery = $event.query;
    const querySplit = this.patientQuery.split(' ');
    const quantity = querySplit.filter((part) => part.length >= 3);
    if (quantity.length > 0) {
      this.subscriptions.push(
        this.service
          .readPatients(this.patientQuery)
          .subscribe(
            (patientsSuggestion) =>
              (this.patientsSuggestion = patientsSuggestion)
          )
      );
    }
  };

  openAdvancedSearch = () => {
    const dialogRef = this.dialogService.open(TurnPatientSearchComponent, {
      data: {
        query: this.patientQuery,
      },
      header: 'Búsqueda avanzada de Pacientes',
      closeOnEscape: true,
      width: '75%',
    });
    const dialogComponent = this.dialogService.dialogComponentRefMap.get(
      this.ref
    )?.instance;
    dialogComponent?.unbindGlobalListeners();

    dialogRef.onClose.subscribe((modalResult: CommonPatientViewModel) => {
      if (modalResult) {
        this.patientHelp = modalResult;
      }
    });
    dialogRef.onDestroy.pipe(first()).subscribe(() => {
      dialogComponent?.moveOnTop();
      dialogComponent?.bindGlobalListeners();
      dialogComponent?.focus();
    });
  };

  openNewPatientModal = () => {
    const dialogRef = this.patientService.openPatientDialog(
      true,
      PatientDialogComponent
    );
    const dialogComponent = this.dialogService.dialogComponentRefMap.get(
      this.ref
    )?.instance;
    dialogComponent?.unbindGlobalListeners();
    dialogRef.onClose
      .pipe(tap(this.patientService.patientOnClose))
      .subscribe((modalResult: CommonPatientViewModel) => {
        if (modalResult) {
          this.patientHelp = modalResult;
        }
      });
    dialogRef.onDestroy.pipe(first()).subscribe(() => {
      dialogComponent?.moveOnTop();
      dialogComponent?.bindGlobalListeners();
      dialogComponent?.focus();
    });
  };

  showSpecialtiesSuggestions = ($event: any) =>
    (this.specialtiesSuggestion = this.specialties
      .filter((specialty) =>
        Utils.containsQuery(
          Utils.normalizeText($event.query).split(' '),
          Utils.normalizeText(specialty.name!)
        )
      )
      .map((specialty) => ({
        label: specialty.name,
        value: {
          id: specialty.id,
          codeAlt: specialty.codeAlt,
          name: specialty.name,
        },
      })));

  loadDoctorsBySpecialty = () => {
    if (this.specialtyHelp!.value.codeAlt?.toLowerCase() === 'odon') {
      this.turnStatusHelp = { value: {} };
    }
    this.enableDoctorSearch = false;
    this.subscriptions.push(
      this.service
        .readDoctorBySpecialty(this.specialtyHelp!.value.id!)
        .subscribe((doctors) => {
          this.enableDoctorSearch = true;
          this.doctors = doctors;
        })
    );
  };

  showDoctorSuggestions = ($event: any) =>
    (this.doctorsSuggestion = this.doctors.filter((doctor) =>
      Utils.containsQuery(
        Utils.normalizeText($event.query).split(' '),
        Utils.normalizeText(doctor.fullName!)
      )
    ));

  showTurnStatusSuggestions = ($event: any) =>
    (this.turnStatusSuggestion = this.turnStatus
      .filter((status) =>
        this.specialtyHelp!.value.codeAlt?.toLowerCase() !== 'odon'
          ? true
          : status.id !== 'turn_status_01'
      )
      .filter((status) =>
        Utils.containsQuery(
          Utils.normalizeText($event.query).split(' '),
          Utils.normalizeText(status.name!)
        )
      )
      .map((status) => ({
        label: status.name,
        value: {
          id: status.id,
          codeAlt: status.codeAlt,
          name: status.name,
        },
      })));

  onTurnStartChanged = (e: any) => {
    const selectedDate = new Date(e.value);
    const minutesToAdd = 1000 * 60 * 15;
    this.turn.turnStart = selectedDate;
    this.turn.turnEnd = new Date(selectedDate.getTime() + minutesToAdd);
  };

  private subscribeUserInfo = (userInfo: SecurityUserLoginModel) => {
    this.userInformation = userInfo;
    this.isDoctorRole = this.userInformation.roles?.isDoctor ?? false;
    if (this.isDoctorRole) {
      this.doctorHelp = {
        id: this.userInformation.doctor,
      };
      this.specialties = this.userInformation.specialties!.map((item) => ({
        id: item.id,
        codeAlt: item.codeAlt,
        name: item.name,
      }));
      if (this.specialties.length === 1) {
        const specialty = this.specialties[0];
        this.specialtyHelp = { value: specialty };
      }
      this.doctorOneSpecialty = this.specialties.length === 1;
    }
  };
}
